import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { MessageCircle, QrCode, CheckCircle2, Loader2, ArrowLeft, Keyboard, Send } from "lucide-react";

const DEFAULT_UI = {
  title: "WhatsApp通知の登録",
  enter: "WhatsApp番号を入力してください",
  placeholder: "例: 090-1234-5678",
  submit: "この番号で登録する",
  done: "登録が完了しました",
  already: "登録済みの番号",
  hint: "打刻・勤怠のお知らせがこの番号のWhatsAppに届きます",
};

export default function WhatsAppRegisterPage() {
  const [searchParams] = useSearchParams();
  const companyParam = (searchParams.get("company") || "").toUpperCase();

  const [step, setStep] = useState("scan"); // scan | form | waiting | done
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [identified, setIdentified] = useState(null);
  const [manualMode, setManualMode] = useState(false);
  const [number, setNumber] = useState("");
  const [result, setResult] = useState(null);

  // auto flow
  const [pendingCode, setPendingCode] = useState("");
  const [waLink, setWaLink] = useState("");

  // manual fallback (QR unreadable)
  const [manualOpen, setManualOpen] = useState(false);
  const [manualCompany, setManualCompany] = useState(companyParam);
  const [manualEmp, setManualEmp] = useState("");

  const scannerRef = useRef(null);
  const ui = identified?.ui || DEFAULT_UI;

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch (e) { /* noop */ }
      try { await scannerRef.current.clear(); } catch (e) { /* noop */ }
      scannerRef.current = null;
    }
    setScanning(false);
  }, []);

  const parseQr = (text) => {
    let employee_id = text;
    let company_code = companyParam;
    if (text && text.startsWith("PRC:")) {
      const parts = text.split(":");
      employee_id = parts[1];
      if (parts[2]) company_code = parts[2];
    }
    return { employee_id, company_code };
  };

  const handleIdentify = useCallback(async ({ employee_id, company_code }) => {
    if (!company_code || !employee_id) {
      toast.error("QRコードを認識できませんでした");
      return false;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/whatsapp/register/identify`, { company_code, employee_id });
      setIdentified({ ...res.data, company_code });
      setManualMode(false);
      setNumber("");
      setStep("form");
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "従業員を確認できませんでした");
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const startScanner = useCallback(async () => {
    if (scannerRef.current) return;
    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      const el = document.getElementById("wa-qr-reader");
      if (!el) { setTimeout(startScanner, 300); return; }
      el.innerHTML = "";
      setScanning(true);
      const scanner = new Html5Qrcode("wa-qr-reader");
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 12, qrbox: { width: 240, height: 240 } },
        async (decodedText) => {
          if (navigator.vibrate) navigator.vibrate(80);
          await stopScanner();
          const ok = await handleIdentify(parseQr(decodedText));
          if (!ok) setTimeout(startScanner, 1500);
        },
        () => { /* scanning */ }
      );
    } catch (err) {
      setScanning(false);
      scannerRef.current = null;
      toast.error("カメラにアクセスできません。QRが読めない場合は手入力をご利用ください。");
      setManualOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleIdentify, stopScanner]);

  useEffect(() => {
    if (step === "scan") {
      startScanner();
    } else {
      stopScanner();
    }
    return () => { stopScanner(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // 自動登録の完了をポーリングで検知
  useEffect(() => {
    if (step !== "waiting" || !pendingCode) return;
    let active = true;
    const iv = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/whatsapp/register/poll`, { params: { code: pendingCode } });
        if (!active) return;
        if (res.data.status === "done") {
          clearInterval(iv);
          setResult({ message: ui.done, name: res.data.name || identified?.name, masked_number: res.data.masked_number });
          setStep("done");
        } else if (res.data.status === "expired") {
          clearInterval(iv);
          toast.error("登録コードの有効期限が切れました。もう一度お試しください。");
          setStep("form");
        }
      } catch (e) { /* keep polling */ }
    }, 2500);
    return () => { active = false; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, pendingCode]);

  const handleManualIdentify = async () => {
    if (!manualCompany.trim() || !manualEmp.trim()) {
      toast.error("企業コードと社員コードを入力してください");
      return;
    }
    await handleIdentify({ employee_id: manualEmp.trim(), company_code: manualCompany.trim().toUpperCase() });
  };

  const handleAutoRegister = async () => {
    setStarting(true);
    try {
      const res = await axios.post(`${API}/whatsapp/register/start`, {
        company_code: identified.company_code,
        employee_id: identified.employee_id,
      });
      const data = res.data;
      if (!data.receiver_configured || !data.wa_link) {
        toast.message("自動登録は現在準備中です。番号を入力して登録してください。");
        setManualMode(true);
        return;
      }
      setPendingCode(data.code);
      setWaLink(data.wa_link);
      setStep("waiting");
      window.open(data.wa_link, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || "登録を開始できませんでした");
    } finally {
      setStarting(false);
    }
  };

  const handleManualSubmit = async () => {
    if (!number.trim()) { toast.error(ui.enter); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/whatsapp/register/submit`, {
        company_code: identified.company_code,
        employee_id: identified.employee_id,
        number,
      });
      setResult(res.data);
      setStep("done");
    } catch (e) {
      toast.error(e.response?.data?.detail || "登録に失敗しました");
    } finally {
      setSubmitting(false);
    }
  };

  const resetAll = () => {
    setIdentified(null);
    setNumber("");
    setResult(null);
    setManualEmp("");
    setManualMode(false);
    setPendingCode("");
    setWaLink("");
    setStep("scan");
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white flex flex-col items-center px-4 py-8" data-testid="wa-register-page">
      <div className="w-full max-w-md">
        {/* Brand */}
        <div className="flex items-center gap-3 mb-6">
          <span className="w-11 h-11 rounded-xl bg-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-900/40">
            <MessageCircle className="w-6 h-6 text-white" />
          </span>
          <div>
            <h1 className="text-lg font-bold leading-tight">{ui.title}</h1>
            <p className="text-xs text-slate-400">PRC勤怠</p>
          </div>
        </div>

        {/* STEP: SCAN */}
        {step === "scan" && (
          <div className="space-y-4" data-testid="wa-register-scan">
            <div className="rounded-2xl border border-slate-700 bg-slate-800/60 p-5">
              <div className="flex items-center gap-2 text-sm text-slate-300 mb-3">
                <QrCode className="w-5 h-5 text-emerald-400" />
                <span>タイムカードのQRコードをカメラにかざしてください</span>
              </div>
              <div
                id="wa-qr-reader"
                className="w-full aspect-square rounded-xl overflow-hidden bg-black/40 border border-slate-700"
              />
              {loading && (
                <div className="flex items-center justify-center gap-2 mt-3 text-sm text-emerald-300">
                  <Loader2 className="w-4 h-4 animate-spin" /> 確認中...
                </div>
              )}
              {!scanning && !loading && (
                <p className="text-xs text-slate-500 mt-3 text-center">カメラを起動しています…</p>
              )}
            </div>

            {/* Manual fallback */}
            <div className="rounded-2xl border border-slate-700 bg-slate-800/40 p-4">
              <button
                type="button"
                onClick={() => setManualOpen((v) => !v)}
                className="flex items-center gap-2 text-sm text-slate-300 w-full"
                data-testid="wa-register-manual-toggle"
              >
                <Keyboard className="w-4 h-4 text-slate-400" />
                QRが読めない場合は手入力
              </button>
              {manualOpen && (
                <div className="mt-3 space-y-3" data-testid="wa-register-manual">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-400">企業コード</Label>
                    <Input
                      value={manualCompany}
                      onChange={(e) => setManualCompany(e.target.value)}
                      placeholder="例: PRC001"
                      className="bg-slate-700 border-slate-600 h-10"
                      data-testid="wa-register-manual-company"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-400">社員コード</Label>
                    <Input
                      value={manualEmp}
                      onChange={(e) => setManualEmp(e.target.value)}
                      placeholder="例: 1001"
                      className="bg-slate-700 border-slate-600 h-10"
                      data-testid="wa-register-manual-emp"
                    />
                  </div>
                  <Button
                    onClick={handleManualIdentify}
                    disabled={loading}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    data-testid="wa-register-manual-submit"
                  >
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    次へ
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* STEP: FORM (auto + manual) */}
        {step === "form" && identified && (
          <div className="space-y-5" data-testid="wa-register-form">
            <div className="rounded-2xl border border-emerald-600/40 bg-emerald-600/10 p-5 text-center">
              <p className="text-xs text-emerald-300/80">{identified.company_name}</p>
              <p className="text-2xl font-bold mt-1" data-testid="wa-register-name">{identified.name}</p>
              <p className="text-xs text-slate-400 mt-0.5">社員コード: {identified.employee_code}</p>
              {identified.has_number && (
                <p className="text-xs text-amber-300 mt-2" data-testid="wa-register-existing">
                  {ui.already}: {identified.masked_number}
                </p>
              )}
            </div>

            {!manualMode && (
              <>
                <div className="rounded-2xl border border-slate-700 bg-slate-800/60 p-5 space-y-3">
                  <p className="text-sm text-slate-300 leading-relaxed">
                    下のボタンを押すと<b className="text-emerald-400">WhatsApp</b>が開きます。表示されたメッセージを<b>そのまま送信</b>すると、あなたの番号が自動で登録されます。
                  </p>
                  <Button
                    onClick={handleAutoRegister}
                    disabled={starting}
                    className="w-full h-14 text-base bg-emerald-600 hover:bg-emerald-700"
                    data-testid="wa-register-auto-btn"
                  >
                    {starting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Send className="w-5 h-5 mr-2" />}
                    WhatsAppで登録
                  </Button>
                </div>
                <button
                  type="button"
                  onClick={() => setManualMode(true)}
                  className="flex items-center justify-center gap-1.5 text-sm text-slate-400 w-full"
                  data-testid="wa-register-manual-mode-btn"
                >
                  <Keyboard className="w-4 h-4" /> 番号を手で入力する
                </button>
              </>
            )}

            {manualMode && (
              <div className="space-y-4" data-testid="wa-register-manual-entry">
                <div className="space-y-2">
                  <Label className="text-sm">{ui.enter}</Label>
                  <Input
                    value={number}
                    onChange={(e) => setNumber(e.target.value)}
                    placeholder={ui.placeholder}
                    inputMode="tel"
                    className="bg-slate-800 border-slate-600 h-14 text-xl text-center tracking-wide font-mono"
                    data-testid="wa-register-number-input"
                    autoFocus
                  />
                  <p className="text-xs text-slate-400">{ui.hint}</p>
                </div>
                <Button
                  onClick={handleManualSubmit}
                  disabled={submitting}
                  className="w-full h-13 py-4 text-base bg-emerald-600 hover:bg-emerald-700"
                  data-testid="wa-register-submit-btn"
                >
                  {submitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}
                  {ui.submit}
                </Button>
                <button
                  type="button"
                  onClick={() => setManualMode(false)}
                  className="flex items-center justify-center gap-1.5 text-sm text-slate-400 w-full"
                  data-testid="wa-register-back-auto-btn"
                >
                  <ArrowLeft className="w-4 h-4" /> WhatsApp登録に戻る
                </button>
              </div>
            )}

            <button
              type="button"
              onClick={resetAll}
              className="flex items-center justify-center gap-1.5 text-sm text-slate-500 w-full pt-1"
              data-testid="wa-register-rescan"
            >
              <ArrowLeft className="w-4 h-4" /> QRを読み直す
            </button>
          </div>
        )}

        {/* STEP: WAITING (auto) */}
        {step === "waiting" && identified && (
          <div className="space-y-6 text-center" data-testid="wa-register-waiting">
            <div className="rounded-2xl border border-emerald-600/40 bg-emerald-600/10 p-8">
              <Loader2 className="w-12 h-12 text-emerald-400 animate-spin mx-auto mb-4" />
              <p className="text-lg font-bold">WhatsAppの送信をお待ちしています…</p>
              <p className="text-sm text-slate-300 mt-2">
                開いたWhatsAppで、表示されたメッセージを<b>そのまま送信</b>してください。送信すると自動で登録されます。
              </p>
            </div>
            <Button
              onClick={() => waLink && window.open(waLink, "_blank")}
              variant="outline"
              className="w-full border-emerald-600 text-emerald-400 hover:bg-emerald-600/10"
              data-testid="wa-register-reopen-btn"
            >
              <Send className="w-4 h-4 mr-2" /> WhatsAppが開かない場合はこちら
            </Button>
            <button
              type="button"
              onClick={() => { setManualMode(true); setStep("form"); }}
              className="flex items-center justify-center gap-1.5 text-sm text-slate-400 w-full"
              data-testid="wa-register-waiting-manual-btn"
            >
              <Keyboard className="w-4 h-4" /> うまくいかない場合は番号を手で入力
            </button>
          </div>
        )}

        {/* STEP: DONE */}
        {step === "done" && result && (
          <div className="space-y-6 text-center" data-testid="wa-register-success">
            <div className="rounded-2xl border border-emerald-600/40 bg-emerald-600/10 p-8">
              <div className="w-16 h-16 rounded-full bg-emerald-600 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-9 h-9 text-white" />
              </div>
              <p className="text-xl font-bold">{result.message}</p>
              <p className="text-sm text-slate-300 mt-2">{result.name}</p>
              <p className="text-sm text-slate-400 font-mono mt-1">{result.masked_number}</p>
            </div>
            <Button
              onClick={resetAll}
              variant="outline"
              className="w-full border-slate-600 text-slate-200 hover:bg-slate-800"
              data-testid="wa-register-again-btn"
            >
              別の人を登録する
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
